import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { Observable } from "@apollo/client/utilities";

import AsyncStorage from "@react-native-async-storage/async-storage";

const uri_string = `https://${window.location.host}/api/graphql`;

const httpLink = new HttpLink({ uri: uri_string });

const authLink = setContext(async (_, { headers }) => {
  const accessToken = await AsyncStorage.getItem("accessToken");
  return {
    headers: {
      ...headers,
      authorization: accessToken ? `Bearer ${accessToken}` : "",
    },
  };
});

const REFRESH_MUTATION = `mutation RefreshToken($refreshToken: String!) {\n  refreshToken(refreshToken: $refreshToken) {\n    accessToken\n    refreshToken\n  }\n}`;

let isRefreshing = false;
let pendingRequests: Array<(newAccessToken: string | null) => void> = [];

const addPendingRequest = (cb: (newAccessToken: string | null) => void) => {
  pendingRequests.push(cb);
};

const resolvePendingRequests = async () => {
  const newAccess = await AsyncStorage.getItem("accessToken");
  pendingRequests.forEach((cb) => cb(newAccess || null));
  pendingRequests = [];
};

const rejectPendingRequests = (err?: unknown) => {
  pendingRequests.forEach((cb) => cb(null));
  pendingRequests = [];
};

export const deleteLocalStorageTokens = async () => {
  await AsyncStorage.removeItem("accessToken");
  await AsyncStorage.removeItem("refreshToken");
};

export const performRefresh = async (): Promise<boolean> => {
  const rt = await AsyncStorage.getItem("refreshToken");
  if (!rt) return false;
  try {
    const res = await fetch(uri_string, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ operationName: "RefreshToken", query: REFRESH_MUTATION, variables: { refreshToken: rt } }),
    });
    if (!res.ok) return false;
    const json = await res.json();
    if (json.errors) return false;
    const data = json?.data?.refreshToken;
    if (!data?.accessToken || !data?.refreshToken) return false;
    await AsyncStorage.setItem("accessToken", data.accessToken);
    await AsyncStorage.setItem("refreshToken", data.refreshToken);
    return true;
  } catch {
    return false;
  }
};

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  interface MaybeServerError {
    statusCode?: number;
    result?: { errors?: Array<{ message?: string }> };
  }
  const ne = networkError as MaybeServerError | null | undefined;
  const statusCode = ne?.statusCode;
  const isUnauthorizedMsg = Boolean(graphQLErrors?.some((e) => e.message === "Unauthorized"));
  const isUserNotFoundMsg = Boolean(
    graphQLErrors?.some((e) => e.message === "Context creation failed: User not found")
  );

  const unauthorized = Boolean(statusCode === 401 || isUnauthorizedMsg || (statusCode === 400 && isUserNotFoundMsg));

  if (!unauthorized) {
    return;
  }

  if (operation.operationName === "RefreshToken") {
    deleteLocalStorageTokens();
    return;
  }

  if (isRefreshing) {
    // queue the subsequent failed operations
    return new Observable((observer) => {
      addPendingRequest(async (token: string | null) => {
        if (!token) {
          observer.error(networkError ?? new Error("Unauthorized"));
          return;
        }
        operation.setContext(({ headers = {} }) => ({
          headers: { ...headers, authorization: token ? `Bearer ${token}` : "" },
        }));
        forward(operation).subscribe({
          next: observer.next.bind(observer),
          error: observer.error.bind(observer),
          complete: observer.complete.bind(observer),
        });
      });
    });
  }

  isRefreshing = true;
  return new Observable((observer) => {
    // queue the first failed operation
    addPendingRequest(async (token: string | null) => {
      if (!token) {
        observer.error(networkError ?? new Error("Unauthorized"));
        return;
      }
      operation.setContext(({ headers = {} }) => ({
        headers: { ...headers, authorization: token ? `Bearer ${token}` : "" },
      }));
      forward(operation).subscribe({
        next: observer.next.bind(observer),
        error: observer.error.bind(observer),
        complete: observer.complete.bind(observer),
      });
    });

    performRefresh()
      .then(async (success) => {
        isRefreshing = false;
        if (!success) {
          await deleteLocalStorageTokens();
          rejectPendingRequests(networkError ?? new Error("Unauthorized"));
          return;
        }
        await resolvePendingRequests();
      })
      .catch(async (err) => {
        isRefreshing = false;
        await deleteLocalStorageTokens();
        rejectPendingRequests(err);
      });
  });
});

// afterware removed; tokens are managed via refresh flow

export const apolloClient = new ApolloClient({
  link: ApolloLink.from([authLink, errorLink, httpLink]),
  cache: new InMemoryCache(),
});
